import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getFocasHeroValues } from '../../../services/focasHeroApi';

// Polls the real adapter's view of a machine for the ADAPTER + DGNOS system
// screens. One poll feeds both screens (extracted from the old FocasAdapterPanel):
//  - floor/live → streaming.focas (§7 connection status) + the sim overlay
//  - focas-hero proxy → §6 values (engine slice + resolved tool-life addresses)
// Surfaces sim↔adapter drift — the reason both the sim and adapter views exist.
const API_BASE = '/machinesim/api';
const POLL_MS = 2000;

export function useFocasAdapter(machineName, company = 'default', enabled = true) {
  const [values, setValues] = useState(null); // §6 values
  const [focas, setFocas] = useState(null); // streaming.focas (§7)
  const [running, setRunning] = useState(null); // sim overlay from floor/live
  const [reachable, setReachable] = useState(true);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const poll = async () => {
      try {
        const flr = await axios.get(`${API_BASE}/floor/live`, { params: { company } });
        if (alive) {
          const m = (Array.isArray(flr.data) ? flr.data : []).find((x) => x.machine === machineName);
          setFocas(m?.streaming?.focas ?? null);
          setRunning(m?.running ?? null);
        }
      } catch {
        /* floor/live optional for this panel */
      }

      try {
        const v = await getFocasHeroValues(machineName, company);
        if (alive) {
          setValues(v);
          setReachable(true);
          setErr(null);
        }
      } catch (e) {
        // ANY fetch failure means we can't trust the adapter — show "unreachable"
        // rather than sticking on "Waiting…".
        if (alive) {
          setReachable(false);
          setErr(e?.response?.data?.error || (e?.response?.status === 503 ? 'focas-hero unreachable' : e.message));
          setValues(null);
        }
      }
    };
    poll();
    timer.current = setInterval(poll, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer.current);
    };
  }, [machineName, company, enabled]);

  const engine = values?.engine || null;
  const live = !!focas?.live;
  const source = focas?.source || 'none';

  // sim↔adapter drift: what the engine is running vs what the adapter reports.
  const simExec = running?.execution ?? (running?.isActive ? 'ACTIVE' : null);
  const adExec = engine?.execution ?? null;
  const execDrift = simExec && adExec && simExec !== adExec;
  const simParts = running?.partCount;
  const adParts = engine?.part_count;
  const partDrift = Number.isFinite(simParts) && Number.isFinite(adParts) && Math.abs(simParts - adParts) > 1;
  const drift = execDrift || partDrift;

  return {
    values, focas, running, reachable, err,
    engine, live, source,
    drift, execDrift, partDrift, simExec, adExec, simParts, adParts,
  };
}
